type AnalyticsUser = {
  id: number | string;
  role?: { name?: string | null } | null;
};

type PostHogClient = typeof import("posthog-js").default;
type PostHogEvent = {
  properties?: unknown;
  $set?: unknown;
  $set_once?: unknown;
};

/** A property name that holds a URL PostHog fills in itself: the current page, the referrer and their first values. */
const URL_PROPERTY = /(?:url|referrer)$/i;

/** The URL without its query and fragment: they can hold incident ids, invitation and reset tokens. */
function withoutQueryAndFragment(value: string): string {
  const end = value.search(/[?#]/);
  return end === -1 ? value : value.slice(0, end);
}

function sanitizeUrlProperties(properties: unknown): void {
  if (properties === null || typeof properties !== "object") {
    return;
  }
  const record = properties as Record<string, unknown>;
  for (const [key, value] of Object.entries(record)) {
    if (typeof value === "string" && URL_PROPERTY.test(key)) {
      record[key] = withoutQueryAndFragment(value);
    }
  }
}

/**
 * Runs on every event before it leaves the browser. PostHog adds the page and referrer URLs to each event and to
 * the person's first-seen values; only their path stays.
 */
export function sanitizePostHogEvent<T extends PostHogEvent | null>(
  event: T,
): T {
  if (event === null) {
    return event;
  }
  sanitizeUrlProperties(event.properties);
  sanitizeUrlProperties(event.$set);
  sanitizeUrlProperties(event.$set_once);
  for (const nested of ["$set", "$set_once"] as const) {
    if (event.properties !== null && typeof event.properties === "object") {
      sanitizeUrlProperties(
        (event.properties as Record<string, unknown>)[nested],
      );
    }
  }
  return event;
}

const analyticsEnabled =
  import.meta.env.VITE_ENABLE_ANALYTICS === "true" &&
  Boolean(import.meta.env.VITE_POSTHOG_KEY?.trim());
const exceptionCaptureEnabled =
  import.meta.env.VITE_ENABLE_POSTHOG_EXCEPTIONS === "true";
const posthogKey = import.meta.env.VITE_POSTHOG_KEY?.trim() ?? "";
const posthogEnvironment =
  import.meta.env.VITE_POSTHOG_ENVIRONMENT?.trim() ?? "";
// Click autocapture and session replay help explain how engineers move through the app. Both are opt-in per
// deployment because the screens show incident data; replay masks every input and all text.
const autocaptureEnabled = import.meta.env.VITE_POSTHOG_AUTOCAPTURE === "true";
const sessionRecordingEnabled =
  import.meta.env.VITE_POSTHOG_SESSION_RECORDING === "true";
const SESSION_STARTED_STORAGE_KEY = "bitsentry.analytics.sessionStartedFor";
const configuredPostHogHost = import.meta.env.VITE_POSTHOG_HOST?.trim();
let posthogHost = "https://us.i.posthog.com";
if (configuredPostHogHost !== undefined && configuredPostHogHost !== "") {
  posthogHost = configuredPostHogHost;
}

let initialized = false;
let posthogClient: PostHogClient | null = null;
let posthogLoadPromise: Promise<PostHogClient | null> | null = null;

function warnPostHogFailure(action: string, error: unknown): void {
  console.warn(`[posthog] Failed to ${action}`, error);
}

/** The init options. Autocapture and replay stay off unless the deployment turns them on. */
export function buildPostHogInitConfig(flags: {
  autocapture: boolean;
  sessionRecording: boolean;
}) {
  return {
    api_host: posthogHost,
    person_profiles: "identified_only" as const,
    autocapture: flags.autocapture
      ? {
          dom_event_allowlist: [
            "click" as const,
            "submit" as const,
            "change" as const,
          ],
          element_allowlist: [
            "a" as const,
            "button" as const,
            "form" as const,
            "select" as const,
            "label" as const,
          ],
        }
      : false,
    capture_dead_clicks: flags.autocapture,
    rageclick: flags.autocapture,
    capture_pageview: false,
    capture_pageleave: true,
    // Autocapture names an element by its text and attributes; both can show incident data (Task 666).
    mask_all_text: true,
    mask_all_element_attributes: true,
    before_send: sanitizePostHogEvent,
    disable_session_recording: !flags.sessionRecording,
    session_recording: {
      maskAllInputs: true,
      maskTextSelector: "*",
    },
  };
}

function initPostHog(client: PostHogClient): void {
  if (initialized || typeof window === "undefined") {
    return;
  }

  try {
    client.init(
      posthogKey,
      buildPostHogInitConfig({
        autocapture: autocaptureEnabled,
        sessionRecording: sessionRecordingEnabled,
      }),
    );
    client.register({
      product: "dashboard",
      source: "frontend",
      ...(posthogEnvironment === "" ? {} : { environment: posthogEnvironment }),
    });

    if (exceptionCaptureEnabled) {
      client.startExceptionAutocapture({
        capture_unhandled_errors: true,
        capture_unhandled_rejections: true,
      });
    }

    initialized = true;
  } catch (error: unknown) {
    warnPostHogFailure("initialize analytics", error);
  }
}

function loadPostHog(): Promise<PostHogClient | null> {
  if (!analyticsEnabled || typeof window === "undefined") {
    return Promise.resolve(null);
  }

  if (posthogClient !== null) {
    return Promise.resolve(posthogClient);
  }

  if (posthogLoadPromise === null) {
    posthogLoadPromise = import("posthog-js")
      .then(({ default: client }) => {
        initPostHog(client);
        if (!initialized) {
          return null;
        }

        posthogClient = client;
        return client;
      })
      .catch((error: unknown) => {
        warnPostHogFailure("load analytics", error);
        return null;
      });
  }

  return posthogLoadPromise;
}

function enqueueAnalyticsAction(
  action: (client: PostHogClient) => void,
  description: string,
): void {
  void loadPostHog().then((client) => {
    if (client === null) {
      return;
    }

    try {
      action(client);
    } catch (error: unknown) {
      warnPostHogFailure(description, error);
    }
  });
}

export function captureAnalyticsEvent(
  event: string,
  properties?: Record<string, unknown>,
): void {
  if (!analyticsEnabled) {
    return;
  }

  enqueueAnalyticsAction(
    (client) => client.capture(event, properties),
    `capture ${event}`,
  );
}

export function captureAnalyticsException(error: unknown): void {
  if (!analyticsEnabled || !exceptionCaptureEnabled) {
    return;
  }

  enqueueAnalyticsAction(
    (client) => client.captureException(error),
    "capture exception",
  );
}

export function capturePageview(path: string): void {
  if (!analyticsEnabled || typeof window === "undefined") {
    return;
  }

  const pathWithoutQuery = path.split("?")[0] ?? "";
  const pathWithoutHash = pathWithoutQuery.split("#")[0] ?? "";
  let safePath = "/";
  if (pathWithoutHash !== "") {
    safePath = pathWithoutHash;
  }

  enqueueAnalyticsAction(
    (client) =>
      client.capture("$pageview", {
        $current_url: `${window.location.origin}${safePath}`,
        path: safePath,
      }),
    "capture $pageview",
  );
}

function resetAnalyticsUser(client: PostHogClient): void {
  try {
    window.sessionStorage.removeItem(SESSION_STARTED_STORAGE_KEY);
  } catch {
    // Best-effort analytics only; storage issues should not affect logout.
  }
  client.reset();
}

/**
 * One event per signed-in browser session, whether the engineer just logged in or came back with a remembered
 * session. This is the reliable "who opened the dashboard today" signal; `auth_login_succeeded` only covers new logins.
 */
function captureSessionStartOnce(
  client: PostHogClient,
  user: AnalyticsUser,
): void {
  const userId = String(user.id);
  try {
    if (window.sessionStorage.getItem(SESSION_STARTED_STORAGE_KEY) === userId) {
      return;
    }
    window.sessionStorage.setItem(SESSION_STARTED_STORAGE_KEY, userId);
  } catch {
    // Without storage the event may repeat per page load, which is better than never recording it.
  }
  client.capture("dashboard_session_started", {
    role: user.role?.name ?? undefined,
    entry_path: window.location.pathname,
  });
}

export function syncAnalyticsUser(user: AnalyticsUser | null): void {
  if (!analyticsEnabled) {
    return;
  }

  enqueueAnalyticsAction(
    (client) => {
      if (user === null) {
        resetAnalyticsUser(client);
        return;
      }

      client.identify(String(user.id), {
        role: user.role?.name ?? undefined,
        product: "dashboard",
      });
      captureSessionStartOnce(client, user);
    },
    user === null ? "reset analytics identity" : "identify analytics user",
  );
}
